import { Portal, Box, Flex } from "@chakra-ui/react";
import React from "react";

import Configurator from "components/Configurator/Configurator";
import ProjectNavbar from "components/Navbars/ProjectNavbar";
import Sidebar from "components/Sidebar/Sidebar";
import { ChatDrawer } from "components/ChatDrawer/ChatDrawer";
import MainPanel from "components/Layout/MainPanel";
import PanelContainer from "components/Layout/PanelContainer";
import PanelContent from "components/Layout/PanelContent";
import { VideoModal, SelectedSegments, VideoGrid } from "components/Project";
import { useProject } from "components/Project/hooks";

const Project = () => {
  const {
    // Video state
    currentVideos,
    activeVideoId,
    setActiveVideoId,
    selectedSegments,
    setSelectedSegments,
    hasMore,
    getVideoId,
    totalVideos,

    // UI state
    isSidebarCollapsed,
    setIsSidebarCollapsed,

    // Chakra disclosures
    isVideoOpen,
    onVideoOpen,
    onVideoClose,
    isOpen,
    onOpen,
    onClose,
    isChatOpen,
    onChatOpen,
    onChatClose,

    // Loading and error states
    isLoading,
    error,

    // Paths
    pathsData,
    selectedPath,
    handlePathSelect,

    // Handlers
    onLoadMore,
    onVideoClick,
    handleAddSegment,
    handleSegmentUpdate,
    handleDeleteSegment,

    // Export functionality
    isExportingVideo,
    exportVideoProgress,
    exportVideoResult,
    exportVideoError,
    handleExportVideo,

    isExportingXml,
    exportXmlError,
    handleExportXml,

    // Project info
    projectId,
    searchQuery,
    colorMode,
  } = useProject();

  if (error) {
    return <Box>Error: {error.error}</Box>;
  }

  return (
    <Box
      bg={colorMode === "light" ? "white" : "navy.900"}
      h="calc(100vh - 95px)"
      pb={20}
      position="relative"
    >
      <Box display="flex" h="100%" position="relative">
        <Box flexShrink={0} position="relative">
          <Sidebar
            isSidebarCollapsed={isSidebarCollapsed}
            onVideoClick={onVideoClick}
            setIsSidebarCollapsed={setIsSidebarCollapsed}
          />
        </Box>
        <MainPanel flex="1" h="100%" id="main-panel" overflow="hidden">
          <Portal>
            <ProjectNavbar
              isSidebarCollapsed={isSidebarCollapsed}
              onChatOpen={onChatOpen}
              onOpen={onOpen}
              onPathSelect={handlePathSelect}
              paths={pathsData?.paths || []}
              selectedPath={selectedPath}
              selectedSegments={selectedSegments}
              shouldShowSearch
              totalVideos={totalVideos}
            />
          </Portal>

          <PanelContent h="100%" id="content-container">
            <PanelContainer h="100%" id="inner-content-container">
              <Flex
                flexDirection="column"
                h="100%"
                overflowY="auto"
                pt={{ base: "120px", md: "75px" }}
              >
                <VideoGrid
                  currentVideos={currentVideos}
                  getVideoId={getVideoId}
                  hasMore={hasMore}
                  isLoading={isLoading}
                  onLoadMore={onLoadMore}
                  onVideoClick={onVideoClick}
                />
              </Flex>
            </PanelContainer>
          </PanelContent>
          <Configurator isOpen={isOpen} onClose={onClose} />
        </MainPanel>
        <SelectedSegments
          exportVideoError={exportVideoError}
          exportVideoProgress={exportVideoProgress}
          exportVideoResult={exportVideoResult}
          exportXmlError={exportXmlError}
          isExportingVideo={isExportingVideo}
          isExportingXml={isExportingXml}
          isOpen={isOpen}
          onClose={onClose}
          onExportVideo={handleExportVideo}
          onExportXml={handleExportXml}
          onVideoOpen={onVideoOpen}
          selectedSegments={selectedSegments}
          setActiveVideoId={setActiveVideoId}
          setSelectedSegments={setSelectedSegments}
        />
      </Box>
      {!!isVideoOpen && (
        <VideoModal
          isOpen={isVideoOpen}
          onAddSegment={handleAddSegment}
          onClose={onVideoClose}
          onDeleteSegment={handleDeleteSegment}
          onSegmentUpdate={handleSegmentUpdate}
          recommendedSegments={
            currentVideos.find((v) => v.video_id === activeVideoId)?.segments ||
            []
          }
          searchQuery={searchQuery}
          selectedSegments={selectedSegments}
          setSelectedSegments={setSelectedSegments}
          videoId={activeVideoId}
        />
      )}
      <ChatDrawer
        isOpen={isChatOpen}
        onClose={onChatClose}
        projectId={projectId}
      />
    </Box>
  );
};

export default Project;
